import type {
  MailAdminCandidateDto,
  MailAdminMailboxDto,
  MailAdminMemberDto,
  MailAdminMemberRole,
} from './mailAdminTypes';

/** Domains the backend will host mail for; the UI only ever offers these. */
export const MAIL_DOMAINS = ['mediclynk.com', 'mediclynkos.com'] as const;

export const LOCAL_PART_MAX = 64;

const LOCAL_PART_RE = /^[a-z0-9_-]+(\.[a-z0-9_-]+)*$/;

/**
 * Local part of an address: lowercase letters, digits, dot, underscore, hyphen; 1-64 chars;
 * no leading, trailing or consecutive dots. Returns an error message, or null when valid.
 * Callers pass the trimmed value.
 */
export function validateLocalPart(value: string): string | null {
  if (value.length === 0) return 'Enter the part before the @.';
  if (value.length > LOCAL_PART_MAX) return `Use at most ${LOCAL_PART_MAX} characters.`;
  if (/[A-Z]/.test(value)) return 'Use lowercase letters only.';
  if (value.startsWith('.') || value.endsWith('.')) return 'Cannot start or end with a dot.';
  if (value.includes('..')) return 'Cannot contain two dots in a row.';
  if (!LOCAL_PART_RE.test(value)) {
    return 'Only lowercase letters, digits, dot, underscore and hyphen are allowed.';
  }
  return null;
}

/** Validates a full alias address (already trimmed). `primary` and `existing` block duplicates. */
export function validateAlias(
  value: string,
  opts: { primary?: string; existing?: readonly string[] } = {},
): string | null {
  if (value.length === 0) return 'Enter an alias address.';
  if (/[A-Z]/.test(value)) return 'Use lowercase letters only.';
  const at = value.split('@');
  if (at.length !== 2) return 'Use the form name@domain.';
  const [local, domain] = at;
  const localError = validateLocalPart(local);
  if (localError) return localError;
  if (!(MAIL_DOMAINS as readonly string[]).includes(domain)) {
    return `Domain must be ${MAIL_DOMAINS.join(' or ')}.`;
  }
  if (opts.primary && value === opts.primary.toLowerCase()) {
    return 'That is already the mailbox address.';
  }
  if (opts.existing?.some((a) => a.toLowerCase() === value)) return 'That alias is already added.';
  return null;
}

/** Strips accents and anything outside the allowed local-part alphabet. */
const sanitizeNamePart = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '');

/**
 * What a personal mailbox is expected to be called: first.last (or just first). The server is
 * the authority — it appends a number on a collision — so the UI labels this a preview.
 */
export function previewPersonalLocalPart(firstName: string, lastName: string): string {
  return [sanitizeNamePart(firstName), sanitizeNamePart(lastName)].filter(Boolean).join('.');
}

/** Splits "Ana María Pérez" into a first name and the rest, for form prefill. */
export function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/**
 * Domains on which a user can still get a personal mailbox. A user may own several personal
 * mailboxes (for example andree@mediclynk.com and andree@mediclynkos.com), one per domain.
 */
export function freeMailDomains(candidate: Pick<MailAdminCandidateDto, 'personalMailboxDomains'>): string[] {
  const taken = new Set((candidate.personalMailboxDomains ?? []).map((d) => d.toLowerCase()));
  return MAIL_DOMAINS.filter((d) => !taken.has(d));
}

/**
 * Candidates for a mailbox picker. `onlyWithFreeDomain` is the personal-mailbox flow: someone is
 * offered while they still lack a personal mailbox on at least one domain (the server answers 409
 * for a domain they already have). `excludeUids` hides people already chosen/added.
 */
export function filterCandidates(
  candidates: readonly MailAdminCandidateDto[],
  opts: { query?: string; onlyWithFreeDomain?: boolean; excludeUids?: readonly string[] } = {},
): MailAdminCandidateDto[] {
  const q = (opts.query ?? '').trim().toLowerCase();
  return candidates.filter((c) => {
    if (opts.onlyWithFreeDomain && freeMailDomains(c).length === 0) return false;
    if (opts.excludeUids?.includes(c.uid)) return false;
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });
}

export const countOwners = (members: readonly { role: MailAdminMemberRole }[]) =>
  members.filter((m) => m.role === 'owner').length;

/** A member the UI must not let the admin remove or demote: the only owner left. */
export const isLastOwner = (members: readonly MailAdminMemberDto[], uid: string) =>
  members.some((m) => m.uid === uid && m.role === 'owner') && countOwners(members) === 1;

export const memberLabel = (m: Pick<MailAdminMemberDto, 'uid' | 'name' | 'email'>) =>
  m.name || m.email || m.uid;

export const mailboxSearchText = (m: MailAdminMailboxDto) =>
  [m.address, m.displayName, ...m.aliases].join(' ').toLowerCase();
